"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.FIFOQueue = void 0;
const MIN_EMPTY_SPACE_BEFORE_RESIZE = 25;

/**
 * A simple first in first out queue.
 *
 * This queue works by adding elements to an array an incrementing the starting index when an element is "shift"ed (ie. the element is not actually removed from the internal array).
 * If there is too many elements before the starting index (ie. elements which have already been "shift"ed off the queue) then the internal array resizes so that all elements already
 * "shifted"ed are removed from the internal array.
 */
class FIFOQueue {
  /**
   * Creates a first in first out queue with the given starting elements
   *
   * @param {Array<Element>} startingElements? - the elements to start with, if not passed, the queue will start with no elements
   *
   */
  constructor(startingElements) {
    this.startIndex = 0;
    this.backingArray = startingElements ? [...startingElements] : [];
  }

  /**
   * push - add an element to the end of the queue
   *
   * @param {Element} element - the element to add to the end of the queue
   */
  push(element) {
    this.backingArray.push(element);
  }

  /**
   * shift - get the first element in the queue and advance to queue to the next element
   *
   * @returns {?Element} the first element in the queue
   */
  shift() {
    if (this.startIndex >= this.backingArray.length) {
      return;
    }
    const elementToReturn = this.backingArray[this.startIndex];
    this.startIndex += 1;
    if (this.startIndex > MIN_EMPTY_SPACE_BEFORE_RESIZE && this.startIndex > this.backingArray.length / 2) {
      this.resize();
    }
    return elementToReturn;
  }

  /**
   * resize - remove all elements already `shift`ed out of the queue from the internal array
   */
  resize() {
    if (this.startIndex === 0) {
      return;
    }
    this.backingArray = this.backingArray.slice(this.startIndex);
    this.startIndex = 0;
  }

  /**
   * clear - removes all elements from the queue
   */
  clear() {
    this.backingArray = [];
    this.startIndex = 0;
  }

  /**
   * size - get the number of elements in the queue
   *
   * @returns {number}
   */
  size() {
    return this.backingArray.length - this.startIndex;
  }

  /**
   * isEmpty - true if the queue is empty, false otherwise
   *
   * @return {boolean}
   */
  isEmpty() {
    return this.backingArray.length === this.startIndex;
  }
}
exports.FIFOQueue = FIFOQueue;